import { define } from 'wicked-elements'
import SnapSlider from '@tannerhodges/snap-slider'
import onIntersect from '../lib/on-intersect'
import 'raf-interval/raf-interval'

define('[data-component~="home-slider"]', {
  init() {
    this.$slider = this.element.querySelector('[data-slider]')
    this.$autoplayControl = this.element.querySelector('#autoplay-control')
    this.$bulletsContainer = this.element.querySelector('.bullets-container')
    this.$bullets = Array.from(this.element.querySelectorAll('.slider-bullet'))
    this.autoplayTimer = null
    this.autoplayDuration = 5000
    this.hovered = false
    this.inViewport = false

    // Bound methods
    this.handleSlideChange = this.handleSlideChange.bind(this)
    this.handleAutoplayCheckbox = this.handleAutoplayCheckbox.bind(this)
    this.handleSliderEnter = this.handleSliderEnter.bind(this)
    this.handleSliderExit = this.handleSliderExit.bind(this)
    this.handleSliderFocus = this.handleSliderFocus.bind(this)
    this.handleInterfaceEnter = this.handleInterfaceEnter.bind(this)
    this.handleInterfaceExit = this.handleInterfaceExit.bind(this)
    this.handleBulletClick = this.handleBulletClick.bind(this)
    this.handleModal = this.handleModal.bind(this)
  },
  connected() {
    // Setup slider
    this.setupSlider()

    // Handle autoplay when carousel leaves/enters viewport
    this.setupIntersect()

    // Either start or clear autoplay on checkbox change event
    this.setupAutoplayCheckbox()

    // Handle mouse enter/leave and click for bullets
    this.setupBullets()

    // Handle mouse enter/leave for carousel el
    this.setupHoverAutoplayPause()

    // Handle focus on carousel
    this.setupFocusAutoplayPause()

    // Handle Modal opening
    this.setupModalAutoplayPause()
  },
  disconnected() {
    // Destroy slider
    this.slider.destroy()

    // Clear autoplay
    this.clearAutoplayTimer()

    // Remove listeners
    this.$autoplayControl.removeEventListener(
      'change',
      this.handleAutoplayCheckbox
    )
    this.$bulletsContainer.removeEventListener(
      'mouseenter',
      this.handleInterfaceEnter
    )
    this.$bulletsContainer.removeEventListener(
      'mouseleave',
      this.handleInterfaceExit
    )
    this.$bullets.forEach((bullet) => {
      bullet.removeEventListener('click', this.handleBulletClick)
    })
    this.$slider.removeEventListener('mouseenter', this.handleInterfaceEnter)
    this.$slider.removeEventListener('mouseleave', this.handleInterfaceExit)
    this.$slider.removeEventListener('focus', this.handleSliderFocus)
    window.removeEventListener('cmoa:search-modal', this.handleModal)

    // Remove observer
    if (this.stopObserving) {
      this.stopObserving()
    }
  },
  disableAutoplay() {
    this.clearAutoplayTimer()
    this.$autoplayControl.checked = false

    // Pause videos on slides when autoplay is disabled
    this.slider.slides.forEach((slide) => {
      const hasVideo = slide.querySelector('video')

      if (hasVideo) {
        hasVideo.pause()
      }
    })
  },
  setupSlider() {
    this.slider = new SnapSlider(this.$slider, {
      prev: '[data-prev]',
      next: '[data-next]',
      loop: true,
      on: {
        change: this.handleSlideChange,
      },
    })
  },
  setupIntersect() {
    this.stopObserving = onIntersect(
      this.$slider,
      this.handleSliderEnter,
      this.handleSliderExit
    )
  },
  setupAutoplayCheckbox() {
    this.$autoplayControl.addEventListener(
      'change',
      this.handleAutoplayCheckbox
    )
  },
  startAutoplayTimer() {
    // eslint-disable-next-line
    clearRafInterval(this.autoplayTimer)
    // eslint-disable-next-line
    this.autoplayTimer = setRafInterval(() => {
      this.slider.goto('next')
    }, this.autoplayDuration)
  },
  clearAutoplayTimer() {
    // eslint-disable-next-line
    clearRafInterval(this.autoplayTimer)
  },
  setupBullets() {
    this.$bulletsContainer.addEventListener(
      'mouseenter',
      this.handleInterfaceEnter
    )
    this.$bulletsContainer.addEventListener(
      'mouseleave',
      this.handleInterfaceExit
    )
    this.$bullets.forEach((bullet) => {
      bullet.addEventListener('click', this.handleBulletClick)
    })
  },
  setupHoverAutoplayPause() {
    this.$slider.addEventListener('mouseenter', this.handleInterfaceEnter)
    this.$slider.addEventListener('mouseleave', this.handleInterfaceExit)
  },
  setupFocusAutoplayPause() {
    this.$slider.addEventListener('focus', this.handleSliderFocus)
  },
  setupModalAutoplayPause() {
    window.addEventListener('cmoa:search-modal', this.handleModal)
  },
  handleSlideChange() {
    this.$bullets.forEach((bullet, index) => {
      index + 1 === this.slider.current
        ? bullet.classList.add('is-current')
        : bullet.classList.remove('is-current')
    })

    // Start video over if its a video slide and active, pause other videos if not active
    this.slider.slides.forEach((slide) => {
      const hasVideo = slide.querySelector('video')

      if (hasVideo && slide.classList.contains('is-current')) {
        hasVideo.currentTime = 0

        if (this.$autoplayControl.checked) {
          hasVideo.play()
        }

        return
      }

      if (hasVideo) {
        hasVideo.pause()
        return
      }
    })

    // If autoplay is true, reset the timer on slide change so that you dont get
    // weird instances of the timer switching slides immediately after arriving
    // on another slide from the controls
    if (this.$autoplayControl.checked && !this.hovered && this.inViewport) {
      this.startAutoplayTimer()
    }
  },
  handleAutoplayCheckbox() {
    const newVal = this.$autoplayControl.checked

    if (newVal) {
      this.startAutoplayTimer()

      // Play videos on slides when autoplay starts
      this.slider.slides.forEach((slide) => {
        const hasVideo = slide.querySelector('video')

        if (hasVideo) {
          hasVideo.play()
        }
      })
    } else {
      this.disableAutoplay()
    }
  },
  handleSliderEnter() {
    this.inViewport = true
    if (this.$autoplayControl.checked && !this.hovered) {
      this.startAutoplayTimer()
    }
  },
  handleSliderExit() {
    this.inViewport = false
    this.clearAutoplayTimer()
  },
  handleSliderFocus() {
    this.disableAutoplay()
  },
  handleInterfaceEnter() {
    this.hovered = true
    this.clearAutoplayTimer()
  },
  handleInterfaceExit() {
    this.hovered = false
    if (this.$autoplayControl.checked) {
      this.startAutoplayTimer()
    }
  },
  handleModal(e) {
    if (e.detail.open) {
      this.inViewport = false
      this.clearAutoplayTimer()
    } else {
      this.inViewport = true
      if (this.$autoplayControl.checked) {
        this.startAutoplayTimer()
      }
    }
  },
  handleBulletClick(e) {
    e.preventDefault()
    const index = this.getElementIndex(e.target)
    this.slider.goto(index + 1)
    this.$bullets.forEach((b) => b.classList.remove('is-current'))
    e.target.classList.add('is-current')
  },
  getElementIndex(element) {
    const parent = element.parentNode
    const children = parent.children

    for (let i = 0; i < children.length; i++) {
      if (children[i] === element) {
        return i
      }
    }

    return -1
  },
})
